<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <!-- Cabecalho: titulo + acao principal -->
    <header class="pc-header">
      <span class="pc-eyebrow"><v-icon small color="primary">mdi-briefcase-account-outline</v-icon> ÁREA DO PROCURADOR</span>
      <div class="pc-title-row">
        <h1 class="pc-title">Meus <span>credores</span></h1>
        <v-btn v-if="$root.user" color="primary" depressed class="pc-new" @click="openDialog">
          <v-icon left small>mdi-plus</v-icon> Solicitar associação
        </v-btn>
      </div>
      <p class="pc-subtitle">Credores que você representa, por caso, com a situação de cada procuração.</p>
    </header>

    <login-required-notice v-if="!$root.user" />

    <template v-else>
    <!-- Aviso permanente: associacao (procuracao) nao e credenciamento para assembleia -->
    <div class="pc-notice">
      <v-icon color="darkSecondary" class="pc-notice__icon">mdi-information-outline</v-icon>
      <div>
        <strong>Associação não é credenciamento.</strong>
        Para participar e votar na assembleia de credores, solicite o credenciamento em
        <router-link to="/credenciamentos">Credenciamentos</router-link>, dentro do prazo do edital, mesmo com a associação aprovada.
        <div class="pc-notice__hint">A associação é analisada por caso. Mais de um procurador pode ser aprovado para representar o mesmo credor no mesmo caso.</div>
      </div>
    </div>

    <!-- Filtros: situacao em pilulas + caso -->
    <div class="pc-filters">
      <div class="pc-pills" role="tablist" aria-label="Filtrar por situação">
        <button v-for="option in statusOptions" :key="String(option.id)" type="button" role="tab"
          class="pc-pill" :class="{ 'pc-pill--active': filter.status === option.id }"
          :aria-selected="String(filter.status === option.id)" @click="filter.status = option.id">
          {{ option.name }}
        </button>
      </div>
      <v-autocomplete
        dense
        outlined
        clearable
        hide-details
        background-color="#fff"
        placeholder="Todos os casos"
        :items="filterClients"
        item-text="name"
        item-value="id"
        no-data-text="Nenhum caso encontrado"
        v-model="filter.client_id"
        class="pc-case-filter"
      />
    </div>

    <!-- Lista agrupada por caso -->
    <div v-if="loading && !items.length" class="pc-skeleton">
      <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-avatar-two-line" class="mb-2" />
    </div>

    <div v-else-if="!items.length" class="pc-empty">
      <div class="pc-empty__icon"><v-icon size="34" color="darkSecondary">mdi-account-search-outline</v-icon></div>
      <div class="pc-empty__title">{{ hasFilter ? "Nenhum credor encontrado" : "Você ainda não representa nenhum credor" }}</div>
      <div class="pc-empty__text">
        {{ hasFilter ? "Ajuste os filtros para ver outros credores." : "Solicite a associação enviando a procuração e o documento de identificação do credor." }}
      </div>
      <v-btn v-if="!hasFilter" color="primary" depressed class="pc-new mt-4" @click="openDialog">
        <v-icon left>mdi-plus</v-icon> Solicitar associação
      </v-btn>
    </div>

    <div v-else :class="{ 'pc-list--loading': loading }">
      <section v-for="group in groups" :key="group.name" class="pc-group">
        <button type="button" class="pc-group__header" @click="toggleGroup(group.name)">
          <v-icon small class="pc-group__chevron" :class="{ 'pc-group__chevron--closed': collapsed[group.name] }">mdi-chevron-down</v-icon>
          <span class="pc-group__name">{{ group.name }}</span>
          <span class="pc-group__count">{{ group.items.length }} {{ group.items.length === 1 ? "credor" : "credores" }}</span>
        </button>

        <v-expand-transition>
          <div v-show="!collapsed[group.name]">
            <article v-for="item in group.items" :key="item.id" class="pc-row">
              <div class="pc-avatar" :class="`pc-avatar--${personType(item)}`">{{ initials(item.creditor_name) }}</div>

              <div class="pc-info">
                <router-link v-if="Number(item.status) === 1" :to="`/procurador/credores/${item.id}/resumo`" class="pc-name pc-name--link">{{ item.creditor_name }}</router-link>
                <div v-else class="pc-name">{{ item.creditor_name }}</div>
                <div class="pc-meta">
                  <span>{{ personType(item) === "pj" ? "CNPJ" : "CPF" }} {{ formatCpfCnpj(item.creditor_registration) }}</span>
                  <span class="pc-sep">·</span>
                  <span>Solicitado em {{ item.created_at | defaultDate("DD/MM/YYYY") }}</span>
                </div>
                <div v-if="Number(item.status) === 2 && item.rejection_reason" class="pc-reason">
                  <v-icon x-small color="error">mdi-alert-circle-outline</v-icon> {{ item.rejection_reason }}
                </div>
                <div v-else-if="Number(item.status) === 3 && item.rejection_reason" class="pc-reason pc-reason--regularize">
                  <v-icon x-small>mdi-file-document-refresh-outline</v-icon> <strong>O que regularizar:</strong> {{ item.rejection_reason }}
                </div>
                <div v-if="item.creditor_document_url || item.procuration_document_url" class="pc-docs">
                  <a v-if="item.creditor_document_url" @click="openDocument(item.creditor_document_url)">
                    <v-icon x-small>mdi-card-account-details-outline</v-icon> Documento do credor
                  </a>
                  <a v-if="item.procuration_document_url" @click="openDocument(item.procuration_document_url)">
                    <v-icon x-small>mdi-file-sign</v-icon> Procuração
                  </a>
                </div>
              </div>

              <span class="pc-status" :class="`pc-status--${statusKey(item.status)}`">
                <span class="pc-status__dot"></span>{{ getStatusName(item.status) }}
              </span>

              <div class="pc-actions">
                <!-- Corrige o proprio pedido: a data original da solicitacao e mantida -->
                <v-btn v-if="isComplementable(item)" text rounded small color="#6d28d9" class="pc-action" @click="openEditDialog(item)">
                  <v-icon left small>mdi-file-document-refresh-outline</v-icon> Complementar
                </v-btn>
                <template v-if="Number(item.status) === 1">
                  <v-btn text rounded small color="darkSecondary" class="pc-action" @click="$router.push(`/procurador/casos/${item.client_id}/assembleia`)">
                    <v-icon left small>mdi-account-group-outline</v-icon> Assembleia
                  </v-btn>
                  <v-btn text rounded small color="darkSecondary" class="pc-action" @click="$router.push(`/procurador/credores/${item.id}/cadastro`)">
                    <v-icon left small>mdi-card-account-details-outline</v-icon> Cadastro
                  </v-btn>
                </template>
                <v-menu bottom left offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn icon small :loading="removingId === item.id" v-bind="attrs" v-on="on" aria-label="Mais ações">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="pc-menu">
                    <template v-if="Number(item.status) === 1">
                      <v-list-item @click="$router.push(`/procurador/credores/${item.id}/resumo`)">
                        <v-list-item-icon><v-icon small>mdi-card-account-details-star-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>Resumo do credor</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openBankDialog(item)">
                        <v-list-item-icon><v-icon small>mdi-bank-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>Dados bancários</v-list-item-title>
                      </v-list-item>
                      <v-divider class="my-1" />
                    </template>
                    <v-list-item @click="openEditDialog(item)">
                      <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>Editar associação</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="removeAssociation(item)">
                      <v-list-item-icon><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                      <v-list-item-title class="error--text">Remover associação</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </article>
          </div>
        </v-expand-transition>
      </section>

      <div class="pc-footer">
        <span class="pc-footer__count">{{ rangeLabel }}</span>
        <v-pagination v-if="pageCount > 1" v-model="options.page" :length="pageCount" :total-visible="7" circle color="primary" />
      </div>
    </div>

    </template>

    <v-dialog v-model="dialog.show" max-width="760px" persistent scrollable>
      <v-card class="pc-dlg">
        <div class="pc-dlg__head">
          <div>
            <div class="pc-dlg__title">{{ dialogTitle }}</div>
            <div class="pc-dlg__subtitle">
              {{ isComplementable(dialog.item) ? `Envie o que foi solicitado. O pedido continua o mesmo e mantém a data original (${$options.filters.defaultDate(dialog.item.created_at, "DD/MM/YYYY HH:mm")}).` : (dialog.item ? "Atualize os dados e os documentos desta associação." : "Informe o credor que você representa e a procuração que dá esses poderes.") }}
            </div>
            <div v-if="isComplementable(dialog.item) && dialog.item.rejection_reason" class="pc-dlg__request">
              <strong>{{ Number(dialog.item.status) === 3 ? "O que regularizar" : "Motivo da rejeição" }}:</strong> {{ dialog.item.rejection_reason }}
            </div>
          </div>
          <v-btn icon small :disabled="dialog.loading" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pc-dlg__body">
          <v-form ref="form" v-model="dialog.valid">
            <!-- 1. Caso e credor -->
            <section class="pc-dlg__section">
              <div class="pc-dlg__section-head">
                <span class="pc-dlg__step">1</span>
                <div class="pc-dlg__section-title">Caso e credor</div>
              </div>
              <v-row dense>
                <v-col cols="12">
                  <v-autocomplete
                    dense
                    outlined
                    label="Caso"
                    :items="clients"
                    item-text="name"
                    item-value="id"
                    v-model="dialog.form.client_id"
                    :rules="[$rules.required]"
                    no-data-text="Nenhum caso encontrado"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="7">
                  <v-text-field
                    dense
                    outlined
                    label="Nome completo ou razão social"
                    v-model="dialog.form.creditor_name"
                    :rules="[$rules.required]"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-text-field
                    dense
                    outlined
                    label="CPF/CNPJ"
                    v-model="dialog.form.creditor_registration"
                    v-mask="registrationMask"
                    :rules="registrationRules"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12">
                  <file-drop-field
                    label="Documento de identificação do credor"
                    hint="RG, CNH ou contrato social · PDF, JPG ou PNG até 25 MB"
                    accept="image/png, image/jpeg, application/pdf"
                    v-model="dialog.form.creditor_document"
                    :rules="documentRules"
                  />
                </v-col>
              </v-row>
            </section>

            <!-- 2. Poderes da procuracao (modelo eproc) -->
            <section class="pc-dlg__section">
              <div class="pc-dlg__section-head">
                <span class="pc-dlg__step">2</span>
                <div class="pc-dlg__section-title">
                  Poderes especiais
                  <span class="pc-dlg__count">{{ dialog.form.procuration_powers.length }} de {{ procurationPowers.length }}</span>
                </div>
                <div class="pc-dlg__links">
                  <a @click.prevent="setAllPowers(true)">Marcar todos</a>
                  <span>·</span>
                  <a @click.prevent="setAllPowers(false)">Limpar</a>
                </div>
              </div>
              <div class="pc-dlg__hint">Marque os poderes que constam na procuração. A Administração Judicial vê essa lista na análise.</div>
              <div class="pc-powers">
                <v-checkbox
                  v-for="power in procurationPowers"
                  :key="power.id"
                  dense
                  hide-details
                  class="pc-powers__item"
                  :class="{ 'pc-powers__item--on': dialog.form.procuration_powers.includes(power.id), 'pc-powers__item--key': power.highlight }"
                  :value="power.id"
                  v-model="dialog.form.procuration_powers"
                >
                  <template #label>
                    <span>{{ power.name }}</span>
                  </template>
                </v-checkbox>
              </div>
              <div class="pc-dlg__note" :class="{ 'pc-dlg__note--ok': hasAgcPower }">
                <v-icon small :color="hasAgcPower ? 'success' : 'grey darken-1'">
                  {{ hasAgcPower ? "mdi-check-circle" : "mdi-information-outline" }}
                </v-icon>
                <span v-if="hasAgcPower">
                  Com <strong>Participação em Assembleia de Credores</strong>, os credenciamentos deste credor reaproveitam esta procuração.
                </span>
                <span v-else>
                  Sem <strong>Participação em Assembleia de Credores</strong>, cada credenciamento em assembleia pedirá uma procuração específica.
                </span>
              </div>
            </section>

            <!-- 3. Procuracao: modelo (gerar, assinar no GOV.br, anexar) ou propria -->
            <section class="pc-dlg__section">
              <div class="pc-dlg__section-head">
                <span class="pc-dlg__step">3</span>
                <div class="pc-dlg__section-title">Procuração</div>
              </div>

              <!-- Caso com procuracao modelo: o procurador escolhe entre o modelo e uma procuracao propria (analise manual da AJ). -->
              <div v-if="modelAvailable" class="pc-choice">
                <button type="button" class="pc-choice__opt" :class="{ 'pc-choice__opt--on': !dialog.ownProcuration }" @click="setOwnProcuration(false)">
                  <v-icon small :color="!dialog.ownProcuration ? '#7e55ab' : '#8c8597'">mdi-file-document-edit-outline</v-icon>
                  <span class="pc-choice__text">
                    <strong>Procuração modelo</strong>
                    <small>Gerar preenchida, assinar e anexar{{ isPersonPj ? "" : " · aprovação na hora pelo GOV.br" }}</small>
                  </span>
                </button>
                <button type="button" class="pc-choice__opt" :class="{ 'pc-choice__opt--on': dialog.ownProcuration }" @click="setOwnProcuration(true)">
                  <v-icon small :color="dialog.ownProcuration ? '#7e55ab' : '#8c8597'">mdi-paperclip</v-icon>
                  <span class="pc-choice__text">
                    <strong>Procuração própria</strong>
                    <small>Anexar uma procuração já existente · análise manual da Administração Judicial</small>
                  </span>
                </button>
              </div>

              <div v-if="useModel" class="pc-model">
                <div class="pc-model__banner" :class="{ 'pc-model__banner--manual': isPersonPj }">
                  <v-icon small :color="isPersonPj ? 'warning' : 'success'">{{ isPersonPj ? "mdi-account-search-outline" : "mdi-shield-check-outline" }}</v-icon>
                  <span v-if="isPersonPj">Pessoa jurídica: a procuração assinada pela empresa passa pela análise da Administração Judicial.</span>
                  <span v-else>Pessoa física assinando pelo GOV.br: a associação é aprovada na hora, sem esperar análise.</span>
                </div>
                <div class="pc-model__step" :class="{ 'pc-model__step--done': dialog.generated }">
                  <span class="pc-model__number">
                    <v-icon v-if="dialog.generated" x-small color="white">mdi-check</v-icon>
                    <template v-else>A</template>
                  </span>
                  <div class="pc-model__body">
                    <div class="pc-model__label">Gerar a procuração</div>
                    <div class="pc-model__text">Sai preenchida com o caso, o credor e os poderes marcados acima.</div>
                    <template v-if="dialog.generated">
                      <div class="pc-model__done">
                        <v-icon small color="success">mdi-check-circle</v-icon>
                        Procuração <strong>{{ dialog.generated.document_code }}</strong> gerada. Envie o PDF ao credor para assinar.
                      </div>
                      <v-btn depressed class="pc-btn pc-btn--soft mt-3" @click="downloadGenerated">
                        <v-icon left size="18">mdi-tray-arrow-down</v-icon>Baixar procuração (PDF)
                      </v-btn>
                    </template>
                    <template v-else>
                      <div v-if="dialog.generatedStale" class="pc-model__warn">Os dados mudaram depois de gerar: gere a procuração de novo.</div>
                      <v-btn depressed class="pc-btn pc-btn--primary mt-3" :loading="dialog.generating" :disabled="!canGenerate" @click="generateProcuration">
                        <v-icon left size="18">mdi-file-document-edit-outline</v-icon>Gerar procuração
                      </v-btn>
                      <div v-if="!canGenerate" class="pc-model__text mt-1">Preencha o caso, o nome, o CPF/CNPJ e marque ao menos um poder.</div>
                    </template>
                  </div>
                </div>
                <div class="pc-model__step">
                  <span class="pc-model__number">B</span>
                  <div class="pc-model__body">
                    <div class="pc-model__label">Assinar</div>
                    <div v-if="isPersonPj" class="pc-model__text">A empresa assina o PDF com o certificado digital dela ou do representante.</div>
                    <div v-else class="pc-model__text">
                      O credor assina o PDF no assinador GOV.br
                      (<a href="https://assinador.iti.br" target="_blank" rel="noopener">assinador.iti.br</a>), sem alterar o arquivo.
                    </div>
                  </div>
                </div>
                <div class="pc-model__step">
                  <span class="pc-model__number">C</span>
                  <div class="pc-model__body">
                    <div class="pc-model__label mb-2">Anexar o PDF assinado</div>
                    <file-drop-field
                      hint="Somente o PDF assinado · até 25 MB"
                      accept="application/pdf,.pdf"
                      v-model="dialog.form.procuration_document"
                      :rules="documentRules"
                      :disabled="!dialog.generated"
                      disabled-text="Gere a procuração para liberar o envio do PDF assinado."
                    />
                  </div>
                </div>
              </div>

              <file-drop-field
                v-else
                label="Procuração assinada pelo credor"
                :hint="modelAvailable ? 'PDF, JPG ou PNG até 25 MB · passa pela análise da Administração Judicial' : 'PDF, JPG ou PNG até 25 MB'"
                accept="image/png, image/jpeg, application/pdf"
                v-model="dialog.form.procuration_document"
                :rules="documentRules"
              />
            </section>

            <!-- 4. Outros procuradores na mesma procuracao -->
            <section class="pc-dlg__section pc-dlg__section--last">
              <div class="pc-dlg__section-head">
                <span class="pc-dlg__step pc-dlg__step--optional">4</span>
                <div class="pc-dlg__section-title">
                  Outros procuradores
                  <span class="pc-dlg__optional">opcional</span>
                </div>
                <v-btn depressed small class="pc-btn pc-btn--ghost" @click="addAuthorizedProcurator">
                  <v-icon left size="18">mdi-plus</v-icon>Adicionar
                </v-btn>
              </div>
              <div class="pc-dlg__hint">Só quando a procuração também nomeia outros procuradores para este credor e caso.</div>
              <div
                v-for="(procurator, index) in dialog.form.authorized_procurators"
                :key="`authorized-procurator-${index}`"
                class="pc-other"
              >
                <div class="pc-other__head">
                  <span>Procurador {{ index + 2 }}</span>
                  <v-btn icon x-small color="error" @click="removeAuthorizedProcurator(index)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
                <v-row dense>
                  <v-col cols="12" md="7">
                    <v-text-field
                      dense
                      outlined
                      label="Nome completo"
                      v-model="procurator.name"
                      :rules="[$rules.required]"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                      dense
                      outlined
                      label="CPF"
                      v-model="procurator.registration"
                      v-mask="$masks.cpf"
                      :rules="[$rules.required, $rules.cpf]"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      label="E-mail"
                      v-model="procurator.email"
                      :rules="[procurator.email ? $rules.email : true]"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="7" md="3">
                    <v-text-field
                      dense
                      outlined
                      label="OAB (se advogado)"
                      v-model="procurator.oab"
                      :rules="procurator.oab_state ? [$rules.required] : []"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="5" md="3">
                    <v-select
                      dense
                      outlined
                      label="UF da OAB"
                      :items="states"
                      item-text="name"
                      item-value="name"
                      v-model="procurator.oab_state"
                      :rules="procurator.oab ? [$rules.required] : []"
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>
              </div>
            </section>
          </v-form>
        </v-card-text>

        <div class="pc-dlg__foot">
          <v-btn depressed class="pc-btn pc-btn--ghost" @click="closeDialog" :disabled="dialog.loading">Cancelar</v-btn>
          <v-btn depressed class="pc-btn pc-btn--primary" @click="save" :loading="dialog.loading">
            {{ dialog.item ? "Salvar alterações" : "Enviar associação" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <procurator-bank-dialog v-model="bankDialog.show" :association-id="bankDialog.associationId" :client-id="bankDialog.clientId" />
  </section>
</template>

<script>
import procurationPowers, { AGC_POWER } from "@/common/data/procuration-powers.data";
import FileDropField from "@/components/FileDropField.vue";
import ProcuratorBankDialog from "@/components/ProcuratorBankDialog.vue";
import axios from "axios";
import string from "@/common/util/string";
import states from "@/common/data/states-list.data";

export default {
  name: "ProcuratorCreditors",

  components: { FileDropField, ProcuratorBankDialog },

  data: () => ({
    procurationPowers,
    AGC_POWER,
    loading: false,
    removingId: null,
    bankDialog: { show: false, associationId: null, clientId: null },
    states,
    items: [],
    // Casos do filtro da lista: todos, inclusive os com vinculos de procuradores desligados.
    filterClients: [],
    // Casos para pedir vinculo novo: so os com a operacao ligada.
    clients: [],
    total: 0,
    filter: {
      status: null,
      client_id: null,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
    },
    statusOptions: [
      { id: null, name: "Todos" },
      { id: 0, name: "Pendentes" },
      { id: 1, name: "Aprovados" },
      { id: 2, name: "Rejeitados" },
      { id: 3, name: "Regularizar" },
    ],
    // Casos recolhidos na lista (por nome do caso).
    collapsed: {},
    dialog: {
      show: false,
      valid: true,
      loading: false,
      item: null,
      // Aviso de atuacao provisoria, exibido uma vez por cadastro ao anexar a procuracao.
      noticeShown: false,
      // Procuracao modelo: documento gerado (e os dados usados) e opcao de anexar uma propria.
      generated: null,
      generatedKey: null,
      generatedStale: false,
      ownProcuration: false,
      generating: false,
      form: {
        creditor_name: "",
        client_id: null,
        creditor_registration: "",
        creditor_document: null,
        procuration_document: null,
        authorized_procurators: [],
        procuration_powers: [],
      },
    },
    // Procuracao modelo publicada para o caso escolhido.
    modelStatus: { clientId: null, available: false },
  }),

  computed: {
    dialogTitle() {
      if (this.isComplementable(this.dialog.item)) return "Complementar pedido";
      return this.dialog.item ? "Editar associação" : "Associar credor";
    },

    hasAgcPower() {
      return (this.dialog.form.procuration_powers || []).includes(AGC_POWER);
    },
    modelAvailable() {
      return !this.dialog.item && this.modelStatus.available && Number(this.modelStatus.clientId) === Number(this.dialog.form.client_id);
    },
    useModel() {
      return this.modelAvailable && !this.dialog.ownProcuration;
    },
    isPersonPj() {
      return string.sanitizeCpfCnpj(String(this.dialog.form.creditor_registration || "")).length === 14;
    },
    // Dados que vao na procuracao: mudou depois de gerar, precisa gerar de novo.
    generationKey() {
      const form = this.dialog.form;
      return [form.client_id, string.sanitizeCpfCnpj(String(form.creditor_registration || "")), String(form.creditor_name || "").trim().toUpperCase(), [...(form.procuration_powers || [])].sort().join(",")].join("|");
    },
    canGenerate() {
      const digits = string.sanitizeCpfCnpj(String(this.dialog.form.creditor_registration || ""));
      return !!this.dialog.form.client_id && !!String(this.dialog.form.creditor_name || "").trim() && [11, 14].includes(digits.length) && (this.dialog.form.procuration_powers || []).length > 0;
    },
    // Itens da pagina agrupados por caso, na ordem em que chegam.
    groups() {
      const groups = new Map();
      for (const item of this.items) {
        const name = item.client_name || "Sem caso";
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(item);
      }
      return [...groups.entries()].map(([name, items]) => ({ name, items }));
    },
    hasFilter() {
      return this.filter.status !== null || !!this.filter.client_id;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.options.itemsPerPage));
    },
    rangeLabel() {
      if (!this.total) return "";
      const start = (this.options.page - 1) * this.options.itemsPerPage + 1;
      const end = Math.min(this.total, start + this.items.length - 1);
      return `${start}–${end} de ${this.total} ${this.total === 1 ? "credor" : "credores"}`;
    },
    registrationMask() {
      return this.dialog.form.creditor_registration && this.dialog.form.creditor_registration.length < 15
        ? this.$masks.cpf
        : this.$masks.cnpj;
    },
    registrationRules() {
      return [
        this.$rules.required,
        (this.dialog.form.creditor_registration && this.dialog.form.creditor_registration.length < 15 ? this.$rules.cpf : this.$rules.cnpj),
      ];
    },
    documentRules() {
      // Mesmo limite da API (25 MB por arquivo).
      return this.dialog.item
        ? [this.$rules.fileSize(25)]
        : [this.$rules.required, this.$rules.fileSize(25)];
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.load();
      },
    },
    "dialog.form.client_id"(clientId) {
      this.loadTemplateStatus(clientId);
    },
    generationKey(key) {
      if (this.dialog.generated && key !== this.dialog.generatedKey) {
        this.dialog.generated = null;
        this.dialog.generatedStale = true;
        this.dialog.form.procuration_document = null;
      }
    },
    "dialog.form.procuration_document"(file) {
      // Aviso de atuacao provisoria, uma vez por cadastro novo, assim que a procuracao e anexada.
      if (!file || this.dialog.item || this.dialog.noticeShown) return;
      this.dialog.noticeShown = true;
      this.$dialog.confirm({
        title: "Atenção",
        text: "Atenção: após o envio da procuração, será possível praticar atos em nome do credor, em caráter provisório, até que o documento seja analisado pela Administração Judicial. A apreciação dos pedidos apresentados ficará condicionada à verificação da regularidade da documentação encaminhada. Caso necessário, a Administração Judicial poderá solicitar documentos ou informações complementares. Por essa razão, o procurador deverá acompanhar regularmente o e-mail cadastrado, bem como as notificações disponibilizadas na plataforma.",
        actions: {
          true: "Estou ciente",
        },
      });
    },
    "filter.status"() {
      this.options.page = 1;
      this.load();
    },
    "filter.client_id"() {
      this.options.page = 1;
      this.load();
    },
  },

  created() {
    if (!this.$root.user) return;
    this.loadClients();
    this.load();
  },

  methods: {
    async loadTemplateStatus(clientId) {
      if (!clientId) {
        this.modelStatus = { clientId: null, available: false };
        return;
      }
      try {
        const result = await axios.get("/api/procurators/procurations/template-status", { params: { client_id: clientId } });
        this.modelStatus = { clientId, available: !!result.data.content.available };
      } catch (error) {
        this.modelStatus = { clientId, available: false };
      }
    },

    setOwnProcuration(value) {
      if (this.dialog.ownProcuration === value) return;
      this.dialog.ownProcuration = value;
      this.dialog.form.procuration_document = null;
    },

    // Gera a procuracao preenchida e abre o PDF para o credor assinar.
    async generateProcuration() {
      this.dialog.generating = true;
      try {
        const form = this.dialog.form;
        const result = await axios.post("/api/procurators/procurations", {
          client_id: form.client_id,
          creditor_name: form.creditor_name,
          creditor_registration: string.sanitizeCpfCnpj(form.creditor_registration),
          procuration_powers: form.procuration_powers,
        });
        this.dialog.generated = result.data.content;
        this.dialog.generatedKey = this.generationKey;
        this.dialog.generatedStale = false;
        this.downloadFile(this.dialog.generated.url);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.dialog.generating = false;
      }
    },

    // Baixa de novo a procuracao gerada (link assinado novo, o anterior pode ter expirado).
    async downloadGenerated() {
      const generated = this.dialog.generated;
      if (!generated) return;
      try {
        const result = await axios.get(`/api/procurators/procurations/${generated.id}/file`);
        this.downloadFile(result.data.content.url);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      }
    },

    // O link assinado ja vem como anexo: baixa na propria pagina, sem abrir aba (que ficaria em about:blank).
    downloadFile(url) {
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    openBankDialog(item) {
      this.bankDialog = { show: true, associationId: item.id, clientId: item.client_id };
    },

    setAllPowers(checked) {
      this.dialog.form.procuration_powers = checked ? this.procurationPowers.map((power) => power.id) : [];
    },

    async load() {
      this.loading = true;

      try {
        const search = {};
        if (this.filter.status !== null && this.filter.status !== undefined) search.status = this.filter.status;
        if (this.filter.client_id) search.client_id = this.filter.client_id;

        const result = await axios.get("/api/procurators/creditors", {
          params: {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            search: JSON.stringify(search),
          },
        });

        this.items = result.data.content.data.map((item) => ({
          ...item,
          client_name: item.client ? item.client.name : "N/A",
        }));
        this.total = result.data.content.paging.total;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },

    async loadClients() {
      try {
        const result = await axios.get("/api/clients", {
          params: {
            page: 1,
            limit: 100000,
            search: {},
          },
        });

        const clients = (result.data.content.data || result.data.content || [])
          .slice()
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR"));
        this.filterClients = clients;
        this.clients = clients.filter(
          client => (client.portal_procurators_enabled === undefined
                ? (client.creditor_portal_enabled === undefined || Number(client.creditor_portal_enabled) !== 0)
                : Number(client.portal_procurators_enabled) !== 0)
        );
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      }
    },

    openDialog() {
      this.resetDialogForm();
      this.dialog.show = true;
      this.loadTemplateStatus(this.dialog.form.client_id);
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },

    openEditDialog(item) {
      this.dialog.item = item;
      this.dialog.form = {
        procuration_powers: Array.isArray(item.procuration_powers) ? [...item.procuration_powers] : [],
        creditor_name: item.creditor_name || "",
        client_id: item.client_id || (item.client ? item.client.id : null),
        creditor_registration: this.formatCpfCnpj(item.creditor_registration || ""),
        creditor_document: null,
        procuration_document: null,
        authorized_procurators: this.normalizeAuthorizedProcuratorsForForm(item.authorized_procurators),
      };
      this.dialog.show = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },

    closeDialog() {
      if (this.dialog.loading) return;
      this.resetDialog();
    },

    resetDialog() {
      this.dialog.show = false;
      this.resetDialogForm();
    },

    resetDialogForm() {
      this.dialog.item = null;
      this.dialog.noticeShown = false;
      this.dialog.generated = null;
      this.dialog.generatedKey = null;
      this.dialog.generatedStale = false;
      this.dialog.ownProcuration = false;
      this.dialog.generating = false;
      this.dialog.form = {
        creditor_name: "",
        client_id: this.getDefaultClientId(),
        creditor_registration: "",
        creditor_document: null,
        procuration_document: null,
        authorized_procurators: [],
        procuration_powers: [],
      };
    },

    addAuthorizedProcurator() {
      this.dialog.form.authorized_procurators.push({
        name: "",
        registration: "",
        email: "",
        oab: "",
        oab_state: "",
      });
    },

    removeAuthorizedProcurator(index) {
      this.dialog.form.authorized_procurators.splice(index, 1);
    },

    normalizeAuthorizedProcuratorsForForm(value) {
      const items = Array.isArray(value) ? value : [];
      return items.map((item) => ({
        name: item.name || "",
        registration: this.formatCpfCnpj(item.registration || ""),
        email: item.email || "",
        oab: item.oab || "",
        oab_state: item.oab_state || "",
      }));
    },

    getDefaultClientId() {
      // O caso filtrado so vira padrao se aceitar vinculo novo.
      if (this.filter.client_id && this.clients.some((client) => client.id === this.filter.client_id)) return this.filter.client_id;
      if (this.clients.length === 1) return this.clients[0].id;
      return null;
    },

    buildPayload() {
      const formData = new FormData();
      formData.append("creditor_name", this.dialog.form.creditor_name);
      if (this.dialog.form.client_id) {
        formData.append("client_id", this.dialog.form.client_id);
      }
      formData.append("creditor_registration", string.sanitizeCpfCnpj(this.dialog.form.creditor_registration));
      formData.append("procuration_powers", JSON.stringify(this.dialog.form.procuration_powers || []));

      if (this.dialog.form.creditor_document) {
        formData.append("creditor_document", this.dialog.form.creditor_document);
      }

      if (this.dialog.form.procuration_document) {
        formData.append("procuration_document", this.dialog.form.procuration_document);
      }
      // Procuracao gerada pelo modelo: a API confere o PDF assinado (GOV.br).
      if (this.useModel && this.dialog.generated) {
        formData.append("procuration_document_id", this.dialog.generated.id);
      }

      const authorizedProcurators = this.dialog.form.authorized_procurators.map((item) => ({
        ...item,
        registration: string.sanitizeCpfCnpj(item.registration),
      }));

      formData.append("authorized_procurators", JSON.stringify(authorizedProcurators));

      return formData;
    },

    async save() {
      if (!this.dialog.form.client_id) {
        this.$dialog.notify.error("Selecione o caso antes de salvar a associação.", { position: "top-right", timeout: 5000 });
        return;
      }

      if (!this.$refs.form.validate()) return;

      const complementing = this.isComplementable(this.dialog.item);
      const proceed = await this.$dialog.confirm({
        title: this.dialogTitle,
        text: complementing
          ? "Deseja enviar o complemento? O pedido volta para análise e mantém a data original da solicitação."
          : this.dialog.item
          ? "Deseja atualizar esta associação? Ela voltará para análise."
          : "Deseja solicitar a associação com este credor?",
        actions: {
          false: "Cancelar",
          true: "Salvar",
        },
      });

      if (!proceed) return;

      this.dialog.loading = true;

      try {
        if (this.dialog.item) {
          await axios.patch(`/api/procurators/creditors/${this.dialog.item.id}`, this.buildPayload());
          this.$dialog.notify.success(complementing ? "Complemento enviado. O pedido voltou para análise." : "Associação atualizada com sucesso.", { position: "top-right", timeout: 3000 });
        } else {
          const created = await axios.post("/api/procurators/creditors", this.buildPayload());
          const content = (created.data && created.data.content) || {};
          if (content.procuration_validation === "pass" && Number(content.status) === 1) {
            this.$dialog.notify.success("Procuração validada pelo GOV.br: associação aprovada.", { position: "top-right", timeout: 5000 });
          } else {
            this.$dialog.notify.success("Associação solicitada com sucesso.", { position: "top-right", timeout: 3000 });
          }
          this.resetDialog();
          await this.load();

          // Evita que o procurador confunda a associacao com o credenciamento da assembleia.
          const goToAccreditations = await this.$dialog.confirm({
            title: "Associação solicitada. Atenção ao credenciamento",
            text: "A associação ao credor não é o credenciamento para a assembleia de credores. Se houver assembleia neste caso, o credenciamento deve ser solicitado separadamente na área Credenciamentos, dentro do prazo do edital, mesmo com a associação ainda em análise. Sem o credenciamento, o credor não participa nem vota.",
            actions: {
              false: "Entendi",
              true: "Ir para credenciamentos",
            },
          });

          if (goToAccreditations) this.$router.push("/credenciamentos");
          return;
        }
        this.resetDialog();
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.dialog.loading = false;
      }
    },

    async removeAssociation(item) {
      const proceed = await this.$dialog.confirm({
        title: "Remover associação",
        text: "Deseja remover esta associação com o credor?",
        actions: {
          false: "Cancelar",
          true: "Remover",
        },
      });

      if (!proceed) return;

      this.removingId = item.id;

      try {
        await axios.delete(`/api/procurators/creditors/${item.id}`);
        this.$dialog.notify.success("Associação removida com sucesso.", { position: "top-right", timeout: 3000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.removingId = null;
      }
    },

    toggleGroup(name) {
      this.$set(this.collapsed, name, !this.collapsed[name]);
    },

    personType(item) {
      return string.sanitizeCpfCnpj(String(item.creditor_registration || "")).length === 14 ? "pj" : "pf";
    },

    initials(name) {
      const words = String(name || "").trim().split(/\s+/).filter((word) => word.length > 2);
      return ((words[0] || "")[0] || "?").toUpperCase() + ((words[1] || "")[0] || "").toUpperCase();
    },

    statusKey(status) {
      return { 0: "pending", 1: "approved", 2: "rejected", 3: "regularize" }[Number(status)] || "pending";
    },

    // Rejeitado ou com regularizacao solicitada: complementa o mesmo pedido.
    isComplementable(item) {
      return !!item && [2, 3].includes(Number(item.status));
    },

    openDocument(url) {
      if (!url) return;
      window.open(url, "_blank");
    },

    formatCpfCnpj(value) {
      const normalized = string.sanitizeCpfCnpj(String(value || ""));

      if (normalized.length === 11) {
        return normalized.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }

      if (normalized.length === 14) {
        return normalized.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }

      return value || "N/A";
    },

    getStatusName(status) {
      switch (Number(status)) {
        case 0:
          return "Pendente";
        case 1:
          return "Aprovado";
        case 2:
          return "Rejeitado";
        case 3:
          return "Regularização solicitada";
        default:
          return "N/A";
      }
    },

    getStatusColor(status) {
      if (Number(status) === 1) return "success";
      if (Number(status) === 2) return "error";
      return "warning";
    },
  },
};
</script>

<style scoped>
/* Modal de associacao */
.pc-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px !important; text-transform: none !important; letter-spacing: 0 !important; font-size: 14px !important; font-weight: 600 !important; }
.pc-btn.v-size--small { height: 34px !important; padding: 0 12px !important; font-size: 13.5px !important; }
.pc-btn--primary { background: #1d0b33 !important; color: #fff !important; }
.pc-btn--primary:hover { background: #2f1650 !important; }
.pc-btn--primary.v-btn--disabled { background: #e7e3ed !important; color: #9a93a6 !important; }
.pc-btn--primary.v-btn--disabled .v-icon { color: #9a93a6 !important; }
.pc-btn--soft { background: rgba(126, 85, 171, 0.12) !important; color: #4b2a75 !important; }
.pc-btn--soft:hover { background: rgba(126, 85, 171, 0.2) !important; }
.pc-btn--ghost { background: transparent !important; color: #4b2a75 !important; }
.pc-btn--ghost:hover { background: rgba(126, 85, 171, 0.08) !important; }
.pc-dlg ::v-deep .v-text-field--outlined fieldset { border-radius: 10px; border-color: #d9d2e3; }
.pc-dlg ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) .v-input__control:hover fieldset { border-color: #7e55ab; }
.pc-dlg { border-radius: 14px !important; overflow: hidden; }
.pc-dlg__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 24px 16px; border-bottom: 1px solid #ece8f2; }
.pc-dlg__title { font-size: 20px; font-weight: 700; color: #1d0b33; line-height: 1.3; }
.pc-dlg__subtitle { margin-top: 2px; font-size: 13px; color: #6b6478; }
.pc-dlg__body { padding: 4px 24px 8px !important; background: #fff; }
.pc-dlg__section { padding: 20px 0; border-bottom: 1px solid #f0edf4; }
.pc-dlg__section--last { border-bottom: 0; }
.pc-dlg__section-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.pc-dlg__step { flex: 0 0 24px; height: 24px; border-radius: 50%; background: #1d0b33; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.pc-dlg__step--optional { background: #e7e2ef; color: #5a4d6d; }
.pc-dlg__section-title { flex: 1; min-width: 0; font-size: 15px; font-weight: 700; color: #1d0b33; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pc-dlg__count, .pc-dlg__optional { font-size: 11.5px; font-weight: 600; color: #6b6478; background: #f3f0f7; border-radius: 999px; padding: 2px 8px; }
.pc-dlg__links { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #b3abbf; white-space: nowrap; }
.pc-dlg__links a { color: #7e55ab; font-weight: 600; }
.pc-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 4px; }
.pc-choice__opt { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1.5px solid #e3dded; border-radius: 10px; background: #fff; text-align: left; cursor: pointer; transition: border-color 0.15s, background 0.15s; }
.pc-choice__opt:hover { border-color: #b89bd6; }
.pc-choice__opt--on { border-color: #7e55ab; background: #f6f2fb; }
.pc-choice__opt .v-icon { margin-top: 2px; }
.pc-choice__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.pc-choice__text strong { font-size: 13.5px; color: #1d0b33; }
.pc-choice__text small { font-size: 12px; color: #6b6478; line-height: 1.35; }
@media (max-width: 600px) { .pc-choice { grid-template-columns: 1fr; } }
.pc-dlg__hint { margin: -6px 0 12px 34px; font-size: 12.5px; color: #6b6478; }
.pc-dlg__note { display: flex; align-items: flex-start; gap: 8px; margin-top: 12px; padding: 10px 12px; border-radius: 10px; background: #f6f4f9; font-size: 13px; color: #4a4257; line-height: 1.45; }
.pc-dlg__note .v-icon { margin-top: 1px; }
.pc-dlg__note--ok { background: #ecf7f0; color: #1e6b45; }
.pc-dlg__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 24px; border-top: 1px solid #ece8f2; background: #fbfafc; }
/* Poderes em grade, com destaque nos marcados */
.pc-powers { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.pc-powers__item { margin: 0 !important; padding: 7px 10px !important; border: 1px solid #ece8f2; border-radius: 8px; transition: background 0.15s, border-color 0.15s; }
.pc-powers__item ::v-deep label { font-size: 13.5px !important; color: #2a1a40 !important; line-height: 1.35; }
.pc-powers__item--on { border-color: rgba(126, 85, 171, 0.45); background: rgba(126, 85, 171, 0.06); }
.pc-powers__item--key ::v-deep label { font-weight: 700; }
/* Outros procuradores */
.pc-other { margin-top: 10px; padding: 12px; border: 1px solid #ece8f2; border-radius: 10px; background: #fcfbfd; }
.pc-other__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; font-size: 12.5px; font-weight: 700; color: #5a4d6d; text-transform: uppercase; letter-spacing: 0.03em; }
@media (max-width: 600px) {
  .pc-dlg__head, .pc-dlg__foot { padding-left: 16px; padding-right: 16px; }
  .pc-dlg__body { padding-left: 16px !important; padding-right: 16px !important; }
  .pc-powers { grid-template-columns: 1fr; }
  .pc-dlg__hint { margin-left: 0; }
}
/* Procuracao modelo (gerar, assinar no GOV.br, anexar) */
.pc-model { padding: 4px 16px 14px; border: 1px solid rgba(126, 85, 171, 0.25); border-radius: 12px; background: rgba(120, 119, 229, 0.04); }
.pc-model__banner { display: flex; align-items: flex-start; gap: 8px; margin: 12px 0 4px; font-size: 13px; color: #1e6b45; line-height: 1.45; }
.pc-model__banner--manual { color: #8a5a00; }
.pc-model__step { display: flex; gap: 12px; align-items: flex-start; padding-top: 14px; }
.pc-model__number { flex: 0 0 24px; height: 24px; border-radius: 50%; background: #7e55ab; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.pc-model__step--done .pc-model__number { background: #1e7b4f; }
.pc-model__body { flex: 1; min-width: 0; font-size: 14px; color: #2a1a40; }
.pc-model__label { font-weight: 700; font-size: 14px; }
.pc-model__text { font-size: 13px; color: #5f5870; }
.pc-model__done { margin-top: 4px; font-size: 13px; color: #1e7b4f; }
.pc-model__done a { margin-left: 6px; }
.pc-model__warn { margin-top: 4px; font-size: 13px; color: #a16207; }

/* Layout leve: sem tabela e sem caixas; linhas soltas com respiro, cores do site
   (roxo #180032, lavanda #7877e5/#7E55AB) e status em pilulas suaves. */

/* Cabecalho no padrao de "Assembleias de credores" */
.pc-header {
  padding: 12px 0 32px;
}

.pc-eyebrow {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #766386;
}

.pc-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 14px 0 10px;
}

.pc-title {
  margin: 0;
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 700;
  color: #25113c;
  letter-spacing: -0.035em;
  line-height: 1.2;
}

.pc-title span {
  color: #806697;
}

.pc-subtitle {
  margin: 0;
  color: #73677c;
  font-size: 14px;
}

.pc-new {
  height: 42px !important;
  padding: 0 20px !important;
  border-radius: 10px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 14px;
  font-weight: 600;
}

/* Aviso: faixa lavanda clara, sem borda */
.pc-notice {
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 14px;
  background: rgba(120, 119, 229, 0.09);
  color: #2a1a40;
  font-size: 14px;
  line-height: 1.55;
  margin-bottom: 28px;
}

.pc-notice__icon {
  margin-top: 1px;
}

.pc-notice a {
  color: #7e55ab;
  font-weight: 600;
}

.pc-notice__hint {
  margin-top: 4px;
  color: rgba(42, 26, 64, 0.65);
  font-size: 13px;
}

/* Filtros */
.pc-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.pc-pills {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: #f3f2fb;
  flex-wrap: wrap;
}

.pc-pill {
  border: 0;
  background: transparent;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(24, 0, 50, 0.65);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.pc-pill:hover {
  color: #180032;
}

.pc-pill--active {
  background: #fff;
  color: #180032;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(24, 0, 50, 0.12);
}

.pc-case-filter {
  max-width: 340px;
  width: 100%;
}

/* Filtro de caso: fundo branco e contorno lilas para destacar do fundo */
.pc-case-filter ::v-deep .v-input__slot {
  border-radius: 10px !important;
  min-height: 42px !important;
}

.pc-case-filter ::v-deep fieldset {
  border-color: rgba(126, 85, 171, 0.45);
}

.pc-case-filter:hover ::v-deep fieldset {
  border-color: #7e55ab;
}

.pc-case-filter ::v-deep input::placeholder {
  color: #4a3a5c !important;
  opacity: 1;
}

.pc-case-filter ::v-deep .v-input__icon .v-icon {
  color: #7e55ab;
}

/* Grupo por caso */
.pc-group {
  margin-bottom: 12px;
}

.pc-group__header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 4px;
  cursor: pointer;
  text-align: left;
}

.pc-group__chevron {
  transition: transform 0.2s ease;
}

.pc-group__chevron--closed {
  transform: rotate(-90deg);
}

.pc-group__name {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7e55ab;
}

.pc-group__count {
  font-size: 12px;
  color: rgba(24, 0, 50, 0.5);
}

/* Linha do credor */
.pc-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(24, 0, 50, 0.06);
  margin-bottom: 8px;
  transition: box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.pc-row:hover {
  border-color: rgba(120, 119, 229, 0.35);
  box-shadow: 0 6px 20px rgba(24, 0, 50, 0.07);
}

.pc-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.pc-avatar--pf {
  background: rgba(120, 119, 229, 0.14);
  color: #4b3a8f;
}

.pc-avatar--pj {
  background: rgba(170, 211, 199, 0.45);
  color: #1f5b4b;
}

.pc-info {
  min-width: 0;
}

.pc-name {
  font-size: 15px;
  font-weight: 600;
  color: #180032;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pc-meta {
  margin-top: 2px;
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

.pc-name--link {
  display: block;
  text-decoration: none;
}

.pc-name--link:hover {
  color: #7e55ab;
  text-decoration: underline;
}

.pc-sep {
  margin: 0 6px;
}

.pc-reason {
  margin-top: 6px;
  font-size: 13px;
  color: #b42318;
}

.pc-docs {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 6px;
}

.pc-docs a {
  font-size: 13px;
  color: #7e55ab;
  text-decoration: none;
}

.pc-docs a:hover {
  text-decoration: underline;
}

.pc-docs .v-icon {
  color: inherit;
}

/* Status em pilula suave */
.pc-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.pc-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.pc-status--approved {
  background: #e7f6ee;
  color: #1e7b4f;
}

.pc-status--pending {
  background: #fff4dc;
  color: #a16207;
}

.pc-status--rejected {
  background: #fdecea;
  color: #b42318;
}

.pc-status--regularize {
  background: #f1ebfb;
  color: #6d28d9;
}

.pc-reason--regularize {
  color: #6d28d9;
}

.pc-dlg__request {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f1ebfb;
  color: #4c1d95;
  font-size: 13px;
}

.pc-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.pc-action {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

.pc-menu .v-list-item__icon {
  margin-right: 12px !important;
}

/* Estados */
.pc-list--loading {
  opacity: 0.55;
  transition: opacity 0.2s ease;
}

.pc-empty {
  text-align: center;
  padding: 56px 16px;
}

.pc-empty__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
}

.pc-empty__title {
  font-size: 17px;
  font-weight: 600;
  color: #180032;
}

.pc-empty__text {
  margin-top: 6px;
  color: rgba(24, 0, 50, 0.6);
  font-size: 14px;
}

.pc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.pc-footer__count {
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

/* Celular: status e acoes descem para baixo do nome */
@media (max-width: 760px) {
  .pc-row {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px 14px;
  }

  .pc-status {
    grid-column: 2;
    justify-self: start;
  }

  .pc-actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
    flex-wrap: wrap;
    border-top: 1px solid rgba(24, 0, 50, 0.06);
    padding-top: 8px;
  }

  .pc-name {
    white-space: normal;
  }

  .pc-case-filter {
    max-width: none;
  }
}
</style>
